import {
  ArrowDownToLine,
  ArrowUpFromLine,
  MessageSquare,
  Radio,
  RefreshCw,
  Radar,
} from "lucide-react";
import type { ConnectorManifest } from "@io-spoke/contracts/integrations";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { ProviderLogo } from "./ProviderLogo";

const capabilityDetails = {
  "work.read": { label: "Read work", icon: ArrowDownToLine },
  "work.create": { label: "Create work", icon: ArrowUpFromLine },
  "work.update": { label: "Update work", icon: RefreshCw },
  "work.comment": { label: "Comments", icon: MessageSquare },
  "signal.read": { label: "Read signals", icon: Radar },
  "event.receive": { label: "Events", icon: Radio },
  "conversation.interact": {
    label: "Two-way conversations",
    icon: MessageSquare,
  },
} as const;

function ConnectorCapabilities({
  connector,
}: {
  connector: ConnectorManifest;
}) {
  if (connector.availability !== "available") {
    return (
      <p className="text-xs capitalize text-muted-foreground">
        {connector.category} integration
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {connector.capabilities.map((capability) => {
        const { label, icon: Icon } = capabilityDetails[capability];
        return (
          <Badge variant="secondary" key={capability} icon={<Icon />}>
            {label}
          </Badge>
        );
      })}
    </div>
  );
}

export function IntegrationCatalogCard({
  connector,
  onConfigure,
  onRequest,
  requested,
  requestPending,
  requestsBusy,
}: {
  connector: ConnectorManifest;
  onConfigure: (connector: ConnectorManifest) => void;
  onRequest: (connector: ConnectorManifest) => void;
  requested: boolean;
  requestPending: boolean;
  requestsBusy: boolean;
}) {
  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <ProviderLogo provider={connector} />
          <Badge
            variant={
              connector.availability === "available" ? "default" : "outline"
            }
            className="capitalize"
          >
            {connector.availability === "planned"
              ? "Planned"
              : connector.availability}
          </Badge>
        </div>
        <div>
          <CardTitle>{connector.name}</CardTitle>
          <CardDescription className="mt-1 min-h-10">
            {connector.description}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between gap-4">
        <ConnectorCapabilities connector={connector} />
        {connector.availability === "available" ? (
          <Button
            className="min-h-11 w-full"
            onClick={() => onConfigure(connector)}
          >
            {connector.setupLabel}
          </Button>
        ) : (
          <Button
            className="min-h-11 w-full"
            variant="outline"
            disabled={requestsBusy || requested}
            onClick={() => onRequest(connector)}
            loading={requestPending}
            loadingLabel="Requesting…"
          >
            {requested ? "Requested" : "Request integration"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
